/**
 * Barbados マップ設定
 * Barbados Solo Play 専用の設定値を定義
 */

import type { GoodsCubesConfig, GoodsDisplayConfig, InitialPlacementConfig } from '../../../src/game/utils/goodsSetup';
import type { MapConfig, TileType } from '../../../src/types';

// ============================================================================
// マップ基本設定
// ============================================================================

/**
 * Barbados マップ設定
 */
export const BARBADOS_MAP_CONFIG: MapConfig = {
  name: 'Barbados',
  availableNewCityTiles: ['NEW_CITY_A', 'NEW_CITY_B', 'NEW_CITY_C', 'NEW_CITY_D'] as TileType[],
  defaultPlayerCount: 1,
  maxTurns: 10,
  hasGoodsDisplay: true,
  maxSharesPerTurn: 1,
  maxSharesLimit: 15,  // 株式発行上限15枚
  issueSharesHelpKey: 'barbados',
  isSoloPlay: true,
  initialOrientation: 'landscape',  // 横向き表示（回転ボタン1回押下状態）
};

// ============================================================================
// 商品設定
// ============================================================================

/**
 * Barbados 商品キューブ設定
 * - 紫は除外
 * - 黒は16個（標準より少ない）
 */
export const BARBADOS_CUBES_CONFIG: GoodsCubesConfig = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    yellow: 20,
    black: 16
  },
  excludeColors: ['purple']
};

/**
 * Barbados 商品補充表設定
 */
export const BARBADOS_DISPLAY_CONFIG: GoodsDisplayConfig = {
  lightSideInitialCount: 10,
  darkSideEnabled: false,
  lightSideCityIds: [
    'NORTH_POINT',    // 列1
    'BATHSHEBA',      // 列2
    'BELL_POINT',     // 列3
    'BRIDGETOWN',     // 列4
    'OISTINS',        // 列5
    'CRANE_BEACH'     // 列6
  ],
  lightSideNewCities: [
    { columnNumber: 3, newCityId: 'A' },
    { columnNumber: 4, newCityId: 'B' },
    { columnNumber: 5, newCityId: 'C' },
    { columnNumber: 6, newCityId: 'D' }
  ],
  darkSideNewCities: [
    { columnNumber: 1, newCityId: 'E' },
    { columnNumber: 2, newCityId: 'F' },
    { columnNumber: 3, newCityId: 'G' },
    { columnNumber: 4, newCityId: 'H' }
  ],
  colorRestrictions: [
    { position: 0, excludeColor: 'red' },   // 列1に赤禁止
    { position: 1, excludeColor: 'blue' }   // 列2に青禁止
  ]
};

/**
 * Barbados 初期配置設定
 */
export const BARBADOS_PLACEMENT_CONFIG: InitialPlacementConfig = {
  targetCityColor: 'yellow',  // 黄色の都市（全6都市）
  cubesPerCity: 1
};

// ============================================================================
// ゲーム特有ルール
// ============================================================================

/**
 * Barbados 固有ルール
 */
export const BARBADOS_RULES = {
  /** 初期資金 */
  initialCash: 10,
  /** 初期株式数 */
  initialShares: 2,
  /** 初期機関車レベル */
  initialLocoLevel: 1,
  /** ターンあたりの最大発行可能株式数 */
  maxSharesPerTurn: 1,
  /** アクションマーカーの使用 */
  useActionMarkers: true,
  /** ソロプレイ専用 */
  soloPlayOnly: true
} as const;

// ============================================================================
// UI設定
// ============================================================================

/**
 * Barbados GameStartModal 設定
 *
 * 共通キーは gameStartCommon から取得、マップ固有キーは gameStartBarbados から取得。
 */
export const BARBADOS_START_MODAL_CONFIG = {
  /** i18n マップ固有キープレフィックス */
  i18nKey: 'gameStartBarbados',
  /** 勝利条件セクションのスタイル */
  victoryStyle: 'success' as const,
  /** 勝利/終了条件設定（統一構造） */
  victory: {
    gameEndKey: 'gameEnd',  // 共通キー
    additionalItems: ['buyBackShares', 'remainingCash'],  // マップ固有キー
  },
  /** 特殊ルールフェーズ */
  phases: [
    { num: '1', ruleKey: 'issueSharesRule', descKey: 'issueSharesDesc' },
    { num: '3', ruleKey: 'selectActionsRule', descKey: 'selectActionsDesc', actionsKey: 'selectActionsActions', extraKeys: ['selectActionsMarker', 'selectActionsCannotReselect', 'selectActionsReset'] },
    { num: '9', ruleKey: 'refillGoodsRule', descKey: 'refillGoodsDesc' },
  ],
  /** 戦略ヒント */
  tips: ['tip1', 'tip2', 'tip3', 'tip4'],
};

/**
 * Barbados GameEndScreen 設定
 */
export const BARBADOS_END_SCREEN_CONFIG = {
  /** スコア計算タイプ */
  scoreType: 'cash' as const,
  /** 株式買い戻しコスト（1株あたり） */
  shareBuybackCost: 5,
  /** 勝利判定閾値（0以上で勝利） */
  victoryThreshold: 0,
  /** スコア評価（降順でマッチング） */
  scoreEvaluations: [
    { minScore: 31, key: 'cash31' },
    { minScore: 21, key: 'cash21' },
    { minScore: 11, key: 'cash11' },
    { minScore: 1, key: 'cash1' },
    { minScore: 0, key: 'cash0' },
    { minScore: -Infinity, key: 'defeat' },
  ],
};
